import {
  useCallback, useEffect, useMemo, useRef, useState, type CSSProperties,
} from 'react';
import { createHeroSpeed, HERO_HOVER_SPEED } from './hero-speed';
import { orderedProjects } from '@/lib/projects';
import HeroPathTrack, { type HeroStreamImage } from './HeroPathTrack';
import { useHeroVisible } from '@/lib/shutter/hero-layer-store';

/** Hero: pure interactive visual field. Proximity hover only — no navigation. */
export default function HeroSection() {
  const stageRef = useRef<HTMLDivElement>(null);
  const speed = useRef(createHeroSpeed());
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const heroVisible = useHeroVisible();
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);

  useEffect(() => { if (isMobile) setHoveredId(null); }, [isMobile]);

  useEffect(() => { speed.current.target = hoveredId ? HERO_HOVER_SPEED : 1; }, [hoveredId]);

  const clearHover = useCallback(() => setHoveredId(null), []);

  // Proximity field reports its nearest card per track; last engaged track wins.
  const activeByTrack = useRef<Record<string, string | null>>({});
  const onActiveChange = useCallback((label: string, id: string | null) => {
    activeByTrack.current[label] = id;
    const next = id ?? Object.values(activeByTrack.current).find(Boolean) ?? null;
    setHoveredId(next);
  }, []);

  // Safety cleanup: window blur, tab hidden, pointer leaving the document.
  useEffect(() => {
    const onVis = () => { if (document.hidden) clearHover(); };
    const onOut = (e: PointerEvent) => { if (!e.relatedTarget) clearHover(); };
    window.addEventListener('blur', clearHover);
    document.addEventListener('visibilitychange', onVis);
    document.addEventListener('pointerout', onOut);
    return () => {
      window.removeEventListener('blur', clearHover);
      document.removeEventListener('visibilitychange', onVis);
      document.removeEventListener('pointerout', onOut);
    };
  }, [clearHover]);

  const hoverProps = { speed, hoveredId: isMobile ? null : hoveredId, onActiveChange, compact: isMobile };

  // Interleave projects (round-robin) so neighbours come from different projects.
  const pool = useMemo<HeroStreamImage[]>(() => {
    const lists = orderedProjects.map((p) => p.images.filter((im) => im.hero));
    const max = Math.max(...lists.map((l) => l.length));
    const out: HeroStreamImage[] = [];
    for (let r = 0; r < max; r += 1) {
      lists.forEach((l, pi) => { if (l[r]) out.push({ image: l[r], project: orderedProjects[pi] }); });
    }
    return out;
  }, []);

  return (
    <section id="top" aria-labelledby="hero-title" aria-hidden={!heroVisible} className={`hero-layer fixed inset-x-0 bottom-0 top-16 z-30 overflow-hidden bg-background-50 ${heroVisible ? '' : 'is-hidden'}`}>
      <div ref={stageRef} onPointerLeave={clearHover} className="hero-stage relative mx-auto h-full w-full">
        <HeroPathTrack
          stageRef={stageRef}
          pool={pool}
          midRatio={isMobile ? 0.16 : 0.17}
          amplitude={isMobile ? 22 : 42}
          wavelengthRatio={isMobile ? 0.9 : 0.62}
          phase={0}
          duration={60}
          direction={1}
          poolOffset={0}
          seed={0}
          label="Featured photography, upper stream"
          {...hoverProps}
        />

        {/* Calm centre */}
        <div className="pointer-events-none absolute inset-0 flex items-end justify-start px-5 pb-10 md:items-center md:justify-center md:px-6 md:pb-0">
          <div className="pointer-events-auto flex max-w-3xl flex-col items-start text-left md:-translate-y-[6px] md:items-center md:px-4 md:py-6 md:text-center">
            <h1 id="hero-title" className="font-heading text-[32px] font-medium leading-[1.08] tracking-[-0.025em] text-foreground-950 md:text-[52px]">
              <span className="hero-line"><span className="md:whitespace-nowrap" style={{ '--i': 0 } as CSSProperties}>Photography for people,</span></span>
              <span className="hero-line"><span className="md:whitespace-nowrap" style={{ '--i': 1 } as CSSProperties}>brands and stories.</span></span>
            </h1>
            <p className="hero-support mt-4 max-w-sm md:mt-6 text-sm leading-relaxed text-foreground-700">
              Campaign, portrait and editorial photography.
            </p>
          </div>
        </div>

        <HeroPathTrack
          stageRef={stageRef}
          pool={pool}
          midRatio={isMobile ? 0.46 : 0.83}
          amplitude={isMobile ? 24 : 46}
          wavelengthRatio={isMobile ? 1 : 0.7}
          phase={300}
          duration={68}
          direction={-1}
          seed={3}
          poolOffset={Math.floor(pool.length / 2)}
          label="Featured photography, lower stream"
          {...hoverProps}
        />

        {/* Scroll cue */}
        <div aria-hidden="true" className="hero-scroll-cue pointer-events-none absolute bottom-5 left-1/2 z-10 hidden -translate-x-1/2 md:block">
          <div className="flex h-8 w-5 justify-center rounded-full border border-foreground-700/60 pt-1.5">
            <span className="hero-scroll-dot block h-1.5 w-[2px] rounded-full bg-foreground-700" />
          </div>
        </div>
      </div>
    </section>
  );
}